// --- Framework
import React from 'react';
import PropTypes from 'prop-types';

// --- External tools
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';

// --- IO
import API from 'io/API';

// --- Logic
import TranslationKey from 'logic/enums/TranslationKey';
import i18n from 'logic/translation/i18n';

// --- External components
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import { isValidEmailAddress } from '../../../logic/stringOperations';

// --- Style
const styles = theme => ({});


const formRules = Object.freeze({
	username: [
		value => isValidEmailAddress(value) || { i18nKey: TranslationKey.validation_email_invalid },
	],
});

const defaultState = Object.freeze({
	username: '',
	password: '',
	errors: {
		username: [],
		password: [],
	},
	requestError: null,
	isFormDisabled: false,
});

class PasswordReset extends React.Component {
	constructor(props) {
		super(props);

		this.state = { ...defaultState };

		this.onSubmit = this.onSubmit.bind(this);
		this.validateForm = this.validateForm.bind(this);
		this.switchToSignIn = this.switchToSignIn.bind(this);
	}

	async validateForm() {
		let hasErrors = false;

		await this.setState((currentState) => {
			const errors = Object.keys(formRules).reduce((result, field) => {
				result[field] = formRules[field]
					.map(rule => rule(currentState[field]))
					.filter(ruleResult => ruleResult !== true);

				if (result[field].length > 0)
					hasErrors = true;

				return result;
			}, {});

			return { errors };
		});

		return hasErrors;
	}

	async onSubmit(event) {
		if (event != null)
			event.preventDefault();

		await this.setState(currentState => ({
			requestError: null,
			isFormDisabled: true,
			username: currentState.username.toLowerCase(),
			// email: currentState.email.toLowerCase(),
		}));

		const {
			props: {
				onSignedIn,
			},
			state: {
				username,
			}
		} = this;

		const hasErrors = await this.validateForm();
		// Invalid fields.
		if (hasErrors) {
			await this.setState({ isFormDisabled: false });
			return;
		}

			const response = await API.postPasswordReset(username);
			await this.setState({
				isFormDisabled: true,
				infoMessage: {
					message: i18n.language === 'nl'
						? 'De link om uw wachtwoord te resetten is naar uw e-mailadres verzonden. Controleer uw inbox of spamfolder.'
						: 'Password reset link has been sent to your e-mail. Please check inbox or spam folder.'
				}
			});
	}

	switchToSignIn() {
		const { history } = this.props;

		history.push('/sign-in');
	}

	render() {
		const {
			props: {
				t,
				classes,
			},
			state: {
				errors,
				infoMessage,
				isFormDisabled,
			}
		} = this;

		return (
			<Container
				disableGutters
				className="page"
			>
        <Typography
          variant="h5"
          component="h2"
          className="pageTitle"
          style={{ textAlign: 'center', marginBottom: '16px' }}
        >
          {t(TranslationKey.password_reset, { defaultValue: i18n.language === 'nl' ? 'Wachtwoord opnieuw instellen' : 'Password reset' })}
        </Typography>
				<Container
					noValidate
					maxWidth="xs"
					disableGutters
					component="form"
					onSubmit={this.onSubmit}
					className="pageContainer"
				>
					<Grid
						container
						spacing={2}
						justify="center"
						direction="column"
						alignItems="stretch"
						className="content"
					>
						<Grid item>
							<TextField
								fullWidth
								variant="outlined"
								disabled={isFormDisabled}
								value={this.state.username}
								label={t(TranslationKey.email)}
								error={errors.username.length > 0}
								helperText={errors.username.length > 0 ? t(errors.username[0].i18nKey, errors.username[0].values) : null}
								onChange={({ target: { value } }) => this.setState((previousState) => {
									if (errors.username.length > 0)
										return { username: value, errors: { ...previousState.errors, username: [] } };
									return { username: value };
								})}
							/>
						</Grid>
						{infoMessage != null ? (
							<>
								<Grid item>
									<Typography variant="caption" color="primary">
										{infoMessage.message || t(infoMessage.i18nKey, infoMessage.values)}
									</Typography>
								</Grid>
								<Grid item>
									<Button
										fullWidth
										size="large"
										type="submit"
										color="primary"
										disableElevation
										variant="contained"
										disabled={false}
										onClick={this.switchToSignIn}
									>
										{t(TranslationKey.to_sign_in)}
									</Button>
								</Grid>
							</>
						) : null}
						{infoMessage == null ? (
							<Grid item>
								<Button
									fullWidth
									size="large"
									type="submit"
									color="primary"
									disableElevation
									variant="contained"
									disabled={isFormDisabled}
                >
                  {t(TranslationKey.password_reset, { defaultValue: i18n.language === 'nl' ? 'Wachtwoord opnieuw instellen' : 'Password reset' })}
                </Button>
							</Grid>
						) : null}
						<Grid item>
							<Button
								fullWidth
								size="small"
								type="button"
								color="primary"
								variant="text"
								disabled={isFormDisabled}
								onClick={this.switchToSignIn}
							>
								{t(TranslationKey.cancel)}
							</Button>
						</Grid>

					</Grid>
				</Container>
			</Container>
		);
	}
}

PasswordReset.propTypes = {
	t: PropTypes.func,
	history: PropTypes.object.isRequired,
	classes: PropTypes.object.isRequired,
	onSignedIn: PropTypes.func.isRequired,
};

export default compose(
	withTranslation(),
	withStyles(styles),
)(PasswordReset);
